<template>
  <div class="container mx-auto px-4 py-8">
    <div class="max-w-4xl mx-auto">
      <!-- Loading State -->
      <div v-if="pending" class="flex justify-center items-center py-12">
        <div class="loading loading-spinner loading-lg"></div>
      </div>

      <!-- Error State -->
      <div v-else-if="error || !article" class="text-center py-12">
        <div class="text-6xl mb-4">❌</div>
        <h1 class="text-3xl font-bold text-base-content mb-4">
          Artículo no encontrado
        </h1>
        <p class="text-base-content/70 mb-8">
          El artículo que buscas no existe o ha sido movido.
        </p>
        <NuxtLink to="/blog" class="btn btn-primary"> Volver al Blog </NuxtLink>
      </div>

      <!-- Article Content -->
      <article v-else class="prose prose-lg prose-primary max-w-none mx-auto">
        <!-- Back to Blog -->
        <div class="not-prose mb-8">
          <NuxtLink to="/blog" class="btn btn-ghost btn-sm">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Volver al Blog
          </NuxtLink>
        </div>

        <!-- Categories -->
        <div
          class="not-prose flex flex-wrap gap-2 mb-6"
          v-if="article.category"
        >
          <NuxtLink
            :to="`/blog/categoria/${article.category.slug}`"
            class="badge badge-primary"
          >
            {{ article.category.name }}
          </NuxtLink>
        </div>

        <!-- Title -->
        <h1 class="text-4xl font-bold text-base-content mb-6">
          {{ article.title }}
        </h1>

        <!-- Meta Information -->
        <div
          class="not-prose flex flex-col sm:flex-row sm:items-center gap-4 mb-8 text-sm text-base-content/70"
        >
          <div class="flex items-center gap-2" v-if="article.author">
            <div class="avatar placeholder">
              <div class="bg-neutral text-neutral-content rounded-full w-8">
                <span class="text-xs">
                  {{ article.author.name?.charAt(0) || "A" }}
                </span>
              </div>
            </div>
            <span>Por {{ article.author.name }}</span>
          </div>

          <div class="flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <time :datetime="article.publishedAt">
              {{ formatDate(article.publishedAt) }}
            </time>
          </div>

          <div class="flex items-center gap-2" v-if="estimatedReadTime">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{{ estimatedReadTime }} min de lectura</span>
          </div>
        </div>

        <!-- Featured Image -->
        <figure v-if="article.cover" class="not-prose mb-8">
          <img
            :src="getImageUrl(article.cover)"
            :alt="article.cover.alternativeText || article.title"
            class="w-full rounded-lg shadow-lg"
          />
          <figcaption
            v-if="article.cover.caption"
            class="text-center text-sm text-base-content/60 mt-2"
          >
            {{ article.cover.caption }}
          </figcaption>
        </figure>

        <!-- Excerpt -->
        <div
          v-if="article.description"
          class="not-prose bg-base-200 p-6 rounded-lg mb-8"
        >
          <p class="text-lg text-base-content italic leading-relaxed">
            {{ article.description }}
          </p>
        </div>

        <!-- Content -->
        <div
          v-html="
            formatContent(article.content || getBlocksContent(article.blocks))
          "
        ></div>

        <!-- Share Buttons -->
        <div class="not-prose mt-12 pt-8 border-t border-base-300">
          <h3 class="text-lg font-semibold text-base-content mb-4">
            Compartir este artículo
          </h3>
          <div class="flex flex-wrap gap-3">
            <button
              @click="shareOnTwitter"
              class="btn btn-outline btn-sm gap-2"
            >
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
                />
              </svg>
              Twitter
            </button>

            <button
              @click="shareOnFacebook"
              class="btn btn-outline btn-sm gap-2"
            >
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
                />
              </svg>
              Facebook
            </button>

            <button
              @click="shareOnLinkedIn"
              class="btn btn-outline btn-sm gap-2"
            >
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
                />
              </svg>
              LinkedIn
            </button>

            <button @click="copyUrl" class="btn btn-outline btn-sm gap-2">
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              Copiar enlace
            </button>
          </div>
        </div>

        <!-- Related Articles -->
        <div
          v-if="relatedArticles?.length"
          class="not-prose mt-12 pt-8 border-t border-base-300"
        >
          <h3 class="text-lg font-semibold text-base-content mb-6">
            Artículos relacionados
          </h3>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <BlogCard
              v-for="relatedArticle in relatedArticles"
              :key="relatedArticle.id"
              :article="relatedArticle"
              class="scale-95"
            />
          </div>
        </div>
      </article>
    </div>
  </div>
</template>

<script setup>
import { marked } from "marked";

// Configure marked options
marked.setOptions({
  breaks: true, // Convert line breaks to <br>
  gfm: true, // Enable GitHub Flavored Markdown
  headerIds: false, // Disable header IDs for security
  mangle: false, // Don't mangle email addresses
});

const route = useRoute();
const slug = route.params.slug;

const { getArticle, getRelatedArticles } = useStrapi();

// Get article data
const {
  data: article,
  pending,
  error,
} = await useAsyncData(`blog-article-${slug}`, () => getArticle(slug));

// Get related articles based on category
const { data: relatedArticles } = await useAsyncData(
  `blog-related-${slug}`,
  () => {
    if (!article.value) return [];
    return getRelatedArticles(
      article.value.slug,
      article.value.category?.slug,
      3
    );
  },
  {
    watch: [article],
  }
);

// SEO and Meta - static for now to avoid reactivity issues
useHead({
  title: "Blog Soleando",
  meta: [
    {
      name: "description",
      content: "Artículo del blog de Soleando",
    },
  ],
});

// Computed properties
const estimatedReadTime = computed(() => {
  const content =
    article.value?.content || getBlocksContent(article.value?.blocks);
  if (!content) return null;
  const wordsPerMinute = 200;
  const wordCount = content.replace(/<[^>]*>/g, "").split(/\s+/).length;
  return Math.ceil(wordCount / wordsPerMinute);
});

// Get config once at component level
const config = useRuntimeConfig();
const strapiUrl =
  config.public.strapiUrl || "https://blog.desertando.anovagroup.es";

// Helper functions for Strapi v5 direct field access
const getImageUrl = (imageData) => {
  if (imageData?.url) {
    if (imageData.url.startsWith("/")) {
      return `${strapiUrl}${imageData.url}`;
    }
    return imageData.url;
  }
  return "/images/blog-placeholder.jpg";
};

const getBlocksContent = (blocks) => {
  if (!blocks || !Array.isArray(blocks)) return "";

  return blocks
    .map((block) => {
      if (block.__component === "shared.rich-text") {
        return block.body || "";
      }
      return "";
    })
    .join("");
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("es-ES", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatContent = (content) => {
  if (!content) return "";

  // Parse markdown to HTML
  let htmlContent = marked(content);

  // Convert relative image URLs to absolute
  htmlContent = htmlContent.replace(
    /src="\/uploads\//g,
    `src="${strapiUrl}/uploads/`
  );

  return htmlContent;
};

const shareOnTwitter = () => {
  const url = encodeURIComponent(window.location.href);
  const text = encodeURIComponent(article.value?.title || "");
  window.open(
    `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
    "_blank"
  );
};

const shareOnFacebook = () => {
  const url = encodeURIComponent(window.location.href);
  window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, "_blank");
};

const shareOnLinkedIn = () => {
  const url = encodeURIComponent(window.location.href);
  const title = encodeURIComponent(article.value?.title || "");
  window.open(
    `https://www.linkedin.com/sharing/share-offsite/?url=${url}&title=${title}`,
    "_blank"
  );
};

const copyUrl = async () => {
  try {
    await navigator.clipboard.writeText(window.location.href);
    // Toast notification - assuming useToast composable
    console.log("Enlace copiado al portapapeles");
  } catch (err) {
    console.error("Error al copiar el enlace");
  }
};

// Handle 404
if (!pending.value && !article.value) {
  throw createError({
    statusCode: 404,
    statusMessage: "Artículo no encontrado",
  });
}
</script>
